import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, X, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCountry } from "@/lib/CountryContext";
import { Field, Stat } from "./Companies";
import Drawer from "@/components/Drawer";
import ActivityTimeline from "@/components/ActivityTimeline";
import FileManager from "@/components/FileManager";

const SECTORS = [
  "cement", "ready_mix", "water_treatment", "wastewater",
  "waste_to_energy", "desalination", "mining", "industrial", "flue_gas", "other",
];
const EMIRATES = ["AbuDhabi", "Dubai", "Sharjah", "RAK", "Ajman", "UAQ", "Fujairah",
  "Riyadh", "Jazan", "Najran", "EasternProvince", "Tabuk", "Makkah", "Qassim", "Doha"];
const STATUSES = ["upcoming", "tendering", "ongoing", "completed", "on_hold"];

const STATUS_STYLE = {
  upcoming: "border-[#2563EB] text-[#2563EB] bg-blue-50",
  tendering: "border-[#D97706] text-[#D97706] bg-amber-50",
  ongoing: "border-[#059669] text-[#059669] bg-emerald-50",
  completed: "border-gray-400 text-gray-600 bg-gray-50",
  on_hold: "border-red-400 text-red-600 bg-red-50",
};

function fmtMoney(v) {
  if (!v) return "—";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
  return `$${v}`;
}

function emptyForm(country = "UAE") {
  return {
    name: "", sector: "cement", country, emirate: "", location: "",
    status: "upcoming", company_id: null, estimated_value_usd: 0,
    start_year: new Date().getFullYear() + 1, description: "",
    required_equipment: [], lat: null, lng: null,
  };
}

export default function Projects() {
  const { country } = useCountry();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [equipText, setEquipText] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("info");

  const load = async () => {
    const params = { country };
    if (q) params.q = q;
    const [r, c] = await Promise.all([
      api.get("/projects", { params }),
      api.get("/companies", { params: { country } }),
    ]);
    setItems(r.data);
    setCompanies(c.data);
  };

  useEffect(() => { load(); }, [country]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(country));
    setEquipText("");
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm(country), ...item });
    setEquipText((item.required_equipment || []).join(", "));
    setShowModal(true);
  };

  const openDetail = (p) => {
    setActive(p);
    setTab("info");
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.lat = payload.lat ? parseFloat(payload.lat) : null;
      payload.lng = payload.lng ? parseFloat(payload.lng) : null;
      payload.start_year = payload.start_year ? parseInt(payload.start_year) : null;
      payload.estimated_value_usd = parseFloat(payload.estimated_value_usd || 0);
      payload.required_equipment = equipText
        .split(",").map((s) => s.trim()).filter(Boolean);
      if (!payload.company_id) payload.company_id = null;
      if (editing) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(editing ? "Project updated" : "Project created");
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow">Pipeline</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Projects</h1>
          <p className="text-sm text-gray-500 mt-2">{items.length} infrastructure projects tracked</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-project-button">
          <Plus size={16} /> New Project
        </button>
      </div>

      <div className="section-card mb-4 p-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects…"
            data-testid="search-projects-input"
            className="input-stark pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.id} className="section-card p-5 hover:border-[#004B87] transition-colors cursor-pointer" data-testid={`project-card-${p.id}`} onClick={() => openDetail(p)}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`chip ${STATUS_STYLE[p.status]}`}>{p.status.replace("_", " ")}</span>
                  <span className="label-eyebrow">{p.sector.replace(/_/g, " ")}</span>
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight">{p.name}</h3>
              </div>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => openEdit(p)} className="text-gray-400 hover:text-[#004B87] p-1" data-testid={`edit-project-${p.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-600 p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
              <span className="flex items-center gap-1"><MapPin size={11} />{p.emirate || "—"} · {p.location || "—"}</span>
              {p.start_year && <span>{p.start_year}</span>}
            </div>
            {p.description && <p className="text-sm text-gray-600 line-clamp-2 mb-3">{p.description}</p>}
            <div className="flex items-end justify-between pt-3 border-t border-gray-100">
              <div>
                <div className="label-eyebrow">Estimated value</div>
                <div className="font-display text-xl font-bold text-[#004B87]">{fmtMoney(p.estimated_value_usd)}</div>
              </div>
              {p.company_id && (
                <div className="text-right">
                  <div className="label-eyebrow">Owner</div>
                  <div className="text-sm font-semibold">{companyName(p.company_id)}</div>
                </div>
              )}
            </div>
            {p.required_equipment?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.required_equipment.slice(0, 3).map((eq) => (
                  <span key={eq} className="chip border-gray-300 text-gray-600 bg-gray-50">{eq}</span>
                ))}
                {p.required_equipment.length > 3 && (
                  <span className="chip border-gray-300 text-gray-500">+{p.required_equipment.length - 3}</span>
                )}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="lg:col-span-2 text-center text-gray-400 py-16">No projects found</div>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        eyebrow="Project"
        title={active?.name || ""}
        testid="project-drawer"
      >
        {active && (
          <div className="p-6">
            <div className="border-b border-gray-200 mb-6 flex gap-6">
              {[
                { id: "info", label: "Info" },
                { id: "activities", label: "Timeline" },
                { id: "files", label: "Files" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`project-tab-${t.id}`}
                  className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                    tab === t.id ? "text-[#004B87] border-[#004B87]" : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "info" && (
              <div className="space-y-4">
                <Stat label="Sector" value={active.sector?.replace(/_/g, " ")} />
                <Stat label="Status" value={
                  <span className={`chip ${STATUS_STYLE[active.status]}`}>{active.status.replace("_", " ")}</span>
                } />
                <Stat label="Location" value={`${active.emirate || ""}${active.location ? " · " + active.location : ""}`} />
                <Stat label="Owner" value={active.company_id ? (
                  <span className="flex items-center gap-1.5"><Building2 size={12} />{companyName(active.company_id)}</span>
                ) : "—"} />
                <Stat label="Start year" value={active.start_year || "—"} />
                <Stat label="Estimated value" value={<span className="font-display text-lg font-bold text-[#004B87]">{fmtMoney(active.estimated_value_usd)}</span>} />
                {active.description && (
                  <div>
                    <div className="label-eyebrow mb-2">Description</div>
                    <div className="text-sm whitespace-pre-wrap">{active.description}</div>
                  </div>
                )}
                {active.required_equipment?.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">Required equipment</div>
                    <div className="flex flex-wrap gap-1">
                      {active.required_equipment.map((eq) => (
                        <span key={eq} className="chip border-gray-300 text-gray-600 bg-gray-50">{eq}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-gray-200">
                  <button onClick={() => { setDrawerOpen(false); openEdit(active); }} className="btn-ghost" data-testid="drawer-edit-project">
                    <Pencil size={14} /> Edit
                  </button>
                </div>
              </div>
            )}

            {tab === "activities" && <ActivityTimeline entityType="project" entityId={active.id} />}
            {tab === "files" && <FileManager entityType="project" entityId={active.id} />}
          </div>
        )}
      </Drawer>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowModal(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            data-testid="project-form-modal"
            className="bg-white border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
                <div className="font-display text-xl font-bold mt-1">Project</div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-black"><X size={20} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-6">
              <Field label="Name" required>
                <input className="input-stark" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="project-name-input" />
              </Field>
              <Field label="Sector">
                <select className="input-stark" value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}>
                  {SECTORS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Country">
                <input className="input-stark" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </Field>
              <Field label="Emirate / Region">
                <select className="input-stark" value={form.emirate || ""}
                  onChange={(e) => setForm({ ...form, emirate: e.target.value })}>
                  <option value="">—</option>
                  {EMIRATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Location">
                <input className="input-stark" value={form.location || ""}
                  onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </Field>
              <Field label="Status">
                <select className="input-stark" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Owner Company">
                <select className="input-stark" value={form.company_id || ""}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value || null })}>
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Start year">
                <input className="input-stark" type="number" value={form.start_year || ""}
                  onChange={(e) => setForm({ ...form, start_year: e.target.value })} />
              </Field>
              <Field label="Estimated value (USD)">
                <input className="input-stark" type="number" value={form.estimated_value_usd || 0}
                  onChange={(e) => setForm({ ...form, estimated_value_usd: e.target.value })} />
              </Field>
              <Field label="Required equipment (comma-separated)" full>
                <input className="input-stark" value={equipText}
                  onChange={(e) => setEquipText(e.target.value)} placeholder="Bulk Storage Silo, 3-in-1 Bulk Handling System" />
              </Field>
              <Field label="Latitude">
                <input className="input-stark" type="number" step="any" value={form.lat ?? ""}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })} />
              </Field>
              <Field label="Longitude">
                <input className="input-stark" type="number" step="any" value={form.lng ?? ""}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })} />
              </Field>
              <Field label="Description" full>
                <textarea className="input-stark min-h-[100px]" value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-project-button">
                {editing ? "Save changes" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
